import { useMutation } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';

export interface EventDetailsForm {
  // General Information
  title: string;
  hide: boolean;
  autoHideAfterEnd?: boolean;
  autoHideGraceHours?: number;
  /** When true, event appears on the anonymous public events link (parent portal). */
  includeInPublicSchedule?: boolean;
  color: string;
  description: string; // 'An introductory piano class for beginners.';
  location: string | null;
  room: string | null;
  studio?: string;
  instructor: Array<string>;
  tags: Array<string>;
  group: Array<string>;
  session: string;

  // Schedule
  days: Array<string>;
  /** @deprecated Use tuitionBillingCycle instead. */
  oneTime?: boolean;
  startDate: string; // '2024-11-01T00:00:00Z';
  endDate: string; // '2025-05-31T00:00:00Z';
  startTime: string; // '2024-11-01T10:00:00Z';
  endTime?: string; // '2024-11-01T10:00:00Z';
  duration: number; // Duration in minutes

  // Enrollment
  startYear: number;
  startMonth: number;
  endYear: number;
  endMonth: number;
  maxSize: number; // Maximum number of participants
  allowSpecificDatesSelection?: boolean;
  policyGroup: Array<string>; // Policies associated with the event
  costumeFee: number;
  tuitionFee: number;
  registrationStartDate: string; // '2024-10-31T00:00:00Z';
  registrationFeeAmount: number; // 50;
  tuitionBillingCycle: string; // 'monthly' | 'one-time' | 'weekly' | 'bi-weekly' | 'every-x-days';
  billingDay?: number;
  billingFrequencyDays?: number;
  availability: Array<{
    day: string;
    startTime: string;
    endTime: string;
  }>;
  timeConfig: string;
}

interface CreateEventParams {
  file: Blob | string;
  fileType: 'file' | 'url';
  eventDetails: EventDetailsForm;
}

export const createEventAPI = async ({
  file,
  fileType,
  eventDetails,
}: CreateEventParams) => {
  const formData = new FormData();

  if (fileType === 'file' && file instanceof Blob) {
    const filename =
      file instanceof File ? file.name : `event-cover-${Date.now()}.jpg`;
    formData.append('file', file, filename);
    formData.append('fileType', fileType);
  } else if (fileType === 'url' && typeof file === 'string') {
    formData.append('fileUrl', file);
    formData.append('fileType', fileType);
  }

  formData.append('payload', JSON.stringify(eventDetails));

  const resp = await baseAPI.post<unknown>('/events', formData);
  return resp.data;
};

export const useCreateEventMutation = () => {
  return useMutation({
    mutationFn: async (params: CreateEventParams) => {
      const response = await createEventAPI(params);
      return response;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['events'],
      });
    },
  });
};
